import React from 'react';

// Initial inline style for the excerpt before its first measurement.
// Hidden while the layout effect computes the dynamic line-clamp and max-height.
const HIDDEN_EXCERPT_STYLE = { WebkitLineClamp: 99, visibility: 'hidden' };

// Narrow cards (dense grids / small screens) shrink the type so headlines
// don't feel oversized. Full tier sizes apply at FULL_WIDTH and above;
// scale falls linearly to MIN_SCALE by NARROW_WIDTH (text-column widths).
const FULL_WIDTH = 340;
const NARROW_WIDTH = 230;
const MIN_SCALE = 0.8;
function widthScale(width) {
  if (!width || width >= FULL_WIDTH) return 1;
  const t = Math.max(0, (width - NARROW_WIDTH) / (FULL_WIDTH - NARROW_WIDTH));
  return MIN_SCALE + (1 - MIN_SCALE) * t;
}

// A two-line title at the base size, and one excerpt line at the base size.
// The tier is measured against these rather than against the rendered title,
// so growing the title can't feed back into the tier that chose it.
const TITLE_REFERENCE_HEIGHT = 20 * 1.4 * 2;
const BASE_EXCERPT_LINE = 14 * 1.55;

// Title font tiers, by the excerpt lines the card has room for and the excerpt
// won't use.
function pickTitleSize(spareLines) {
  if (spareLines >= 3) return 24;
  if (spareLines >= 2) return 22;
  if (spareLines >= 1) return 21;
  return 20;
}

// Excerpt scales proportionally with the title's tier so the type
// hierarchy stays consistent.
function pickExcerptSize(titleSize) {
  if (titleSize >= 24) return 16;
  return 15;
}

/**
 * Sizes the card's text block to the space it actually has.
 *
 * Returns the five refs the caller must attach — `wrapperRef` on the box that
 * bounds the text, `metaRef` / `titleRef` / `excerptRef` on those elements, and
 * `measureRef` on an out-of-flow clone of the excerpt — plus the font sizes and
 * the excerpt's clamp style. Every ref has to be attached: the hook measures
 * against them, and a missing one silently leaves the text at its initial size.
 *
 * `excerptStyle` starts hidden and turns visible on the first successful
 * measurement, which `excerptReady` reports, so the unclamped text never paints.
 */
export default function useCardTextFit(excerpt) {
  const wrapperRef = React.useRef(null);
  const metaRef = React.useRef(null);
  const titleRef = React.useRef(null);
  const excerptRef = React.useRef(null);
  const measureRef = React.useRef(null);

  const [titleSize, setTitleSize] = React.useState(20);
  const [excerptSize, setExcerptSize] = React.useState(15);
  const [excerptStyle, setExcerptStyle] = React.useState(HIDDEN_EXCERPT_STYLE);

  // Pick a title font tier from the room the excerpt leaves unused: an excerpt
  // that can't fill the body area lets the title grow into it.
  React.useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;
    const hasExcerpt = Boolean(excerpt);
    const measure = measureRef.current;
    const meta = metaRef.current;
    const compute = () => {
      let lines = 0;
      let lineHeight = BASE_EXCERPT_LINE;
      if (hasExcerpt && measure) {
        lineHeight = parseFloat(getComputedStyle(measure).lineHeight);
        if (!lineHeight) return;
        lines = Math.max(1, Math.round(measure.getBoundingClientRect().height / lineHeight));
      }
      const metaHeight = meta ? meta.offsetHeight : 0;
      const room = wrapper.clientHeight - TITLE_REFERENCE_HEIGHT - metaHeight;
      const tierTitle = pickTitleSize(Math.floor(room / lineHeight) - lines);
      const scale = widthScale(wrapper.clientWidth);
      setTitleSize(Math.round(tierTitle * scale));
      setExcerptSize(Math.round(pickExcerptSize(tierTitle) * scale));
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(wrapper);
    if (measure) observer.observe(measure);
    if (meta) observer.observe(meta);
    return () => observer.disconnect();
  }, [excerpt]);

  // Dynamic line-clamp: the excerpt takes every line left by title + meta and
  // truncates at the last one. The explicit max-height (N × line-height)
  // keeps subpixel rounding from leaking a sliver of line N+1.
  React.useLayoutEffect(() => {
    const excerptEl = excerptRef.current;
    const wrapper = wrapperRef.current;
    const title = titleRef.current;
    const meta = metaRef.current;
    if (!excerptEl || !wrapper || !title || !excerpt) return undefined;
    const blockHeight = (el) => {
      const style = getComputedStyle(el);
      return el.offsetHeight
        + parseFloat(style.marginTop)
        + parseFloat(style.marginBottom);
    };
    const compute = () => {
      const lineHeight = parseFloat(getComputedStyle(excerptEl).lineHeight);
      if (!lineHeight) return;
      const titleHeight = blockHeight(title);
      const metaHeight = meta ? blockHeight(meta) : 0;
      const available = wrapper.clientHeight - titleHeight - metaHeight;
      // Not enough room for even one full line — hide entirely.
      if (available < lineHeight) {
        setExcerptStyle({ display: 'none' });
        return;
      }
      const lines = Math.floor(available / lineHeight);
      setExcerptStyle({
        WebkitLineClamp: lines,
        maxHeight: `${Math.ceil(lines * lineHeight)}px`,
        visibility: 'visible',
      });
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(wrapper);
    observer.observe(title);
    if (meta) observer.observe(meta);
    return () => observer.disconnect();
  }, [excerpt, titleSize, excerptSize]);

  return {
    wrapperRef,
    metaRef,
    titleRef,
    excerptRef,
    measureRef,
    titleSize,
    excerptSize,
    excerptStyle,
    excerptReady: excerptStyle.visibility === 'visible',
  };
}
